<template>
  <div class="full">
    <ComponentSidebarV2 @on-drag-start="onDragComponent" />
    <div class="form-pages">
      <div class="page" ref="page" @click="unselectComponents()">
        <div class="page-row" v-for="(row, rowIdx) of layoutRows" :key="row.key"
          @dragover.prevent="onDragOver($event, rowIdx)" @drop="onDropBetween($event, rowIdx)"
          @dragleave="onDragLeave()">
          <div class="row-columns" :data-marker="getMarkerForRow(rowIdx)">
            <div class="row-column" v-for="(component, colIdx) of row.components" :key="colIdx" draggable="true"
              @dragstart="onDragComponent(component)" @dragend="onDragEnd"
              @click.stop="onClickComponent($event, component)" :data-marker="getMarkerForCol(rowIdx, colIdx)"
              :data-dragging="component == draggingComponent" :data-selected="isComponentSelected(component)"
              :data-conditional="component.condition != null">
              <HtmlComponent :component="component" :interactable="false" :showTrash="isComponentSelected(component)"
                @deleteComponent="deleteComponents" />
            </div>
          </div>
        </div>
        <div class="drop-zone" @dragover.prevent @drop="onDropAfter" @dragover="onDragOverAfter">
        </div>
      </div>
      <div class="button-container">
        <button class="button primary" @click="attemptSaveChanges">
          <span v-if="pending">Saving changes...</span>
          <span v-else>Save Changes</span>
        </button>
        <a href="#" v-if="formInfo && formInfo.isUnderReview" style="margin-top: 10px"
          @click.prevent="startFormReview">Complete
          Form Review</a>
      </div>
    </div>
    <PropertiesSidebarV2 :emrJson="emrJson" :components="getSelectedComponents()" :all-components="getAllComponents()"
      @update-components="handleUpdateComponents" />
  </div>
</template>

<script>
import * as api from '../../api'
import ComponentSidebarV2 from '@/components/form-editor-html/ComponentSidebarV2.vue';
import HtmlComponent from '../../components/form-components-html/HtmlComponent.vue';
import PropertiesSidebarV2 from '@/components/form-editor-html/PropertiesSidebarV2.vue';
import html2canvas from 'html2canvas'
import { sanitizeLayoutKeys } from '@/utils/mongoKeySanitizer.js'

export default {
  name: 'HTMLFormEditorView',
  data() {
    return {
      form: null,
      layoutRows: [],
      emrJson: null,
      draggingComponent: null,
      draggingRowIndex: -1,
      draggingInfo: null,
      selectedComponentIds: new Set(),
      pending: false
    }
  },
  mounted() {
    this.fetchFormInfo();
  },
  methods: {
    async fetchFormInfo() {
      const formId = this.$route.params.id;
      try {
        this.formInfo = await api.latestVersion(formId);
        this.emrJson = await api.getPublicJson('/emrs/practicefusion.json')

        // Get layout rows and sanitize MongoDB keys
        const originalLayoutRows = this.formInfo.layoutRows || [];
        const sanitizationResult = sanitizeLayoutKeys(originalLayoutRows);

        this.layoutRows = sanitizationResult.sanitized;

        // Notify user if keys were sanitized
        if (sanitizationResult.changesMade) {
          console.warn('Some component keys were automatically sanitized to be MongoDB-compatible:', sanitizationResult.changes);
          alert(
            `Found ${sanitizationResult.changes.length} invalid component key(s) that were automatically fixed.\n\n` +
            `Invalid characters (. and $) have been removed from keys.\n\n` +
            `Please review the changes and save the form.`
          );
        }

        // Ensure all existing components have internal IDs
        this.ensureInternalIds();
      } catch (err) {
        console.error(err);
        alert('Failed to fetch form info');
      }
    },
    async attemptSaveChanges() {
      try {
        this.pending = true;

        const layoutRows = this.layoutRows.map(row => {
          return {
            ...row,
            singleChild: row.components.some(comp => comp.type == 'HEADER' || comp.type == 'PARAGRAPH' || comp.type == 'IMAGE'),
            components: row.components.map(comp => {
              return {
                ...comp,
                _id: undefined
              }
            })
          }
        })

        await api.updateFormLayout(this.formInfo.id, layoutRows);
        const thmbBlob = await this.getFormAsImage();
        const thmbFile = await api.uploadAccountFile(thmbBlob);
        await api.updateFormThumbnail(this.formInfo.id, thmbFile.id);
        this.$router.push(`/forms`)
      } catch (err) {
        console.error(err);
      } finally {
        this.pending = false;
      }
    },
    async getFormAsImage() {
      // Deselect component
      this.selectedComponentIds.clear();
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1)
      })

      const pageEl = this.$refs.page;
      pageEl.style.boxShadow = 'none'; // disable box shadow to prevent rendering issue
      const canvas = await html2canvas(pageEl);
      pageEl.style.boxShadow = '';

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        })
      })
    },
    async startFormReview() {
      if (confirm('Please only proceed if you are authorized as a form reviewer')) {
        const name = prompt('Enter your name:');
        if (name) {
          await api.reviewForm(this.formInfo.id, name);
          await this.attemptSaveChanges();
        }
      }
    },
    onDragComponent(component) {
      this.draggingComponent = component;
    },
    onDragOver(event, rowIdx) {
      if (!this.draggingComponent) {
        return;
      }

      const targetRow = this.layoutRows[rowIdx];
      const targetRowEl = event.currentTarget;
      const componentType = this.draggingComponent.type;
      this.draggingRowIndex = rowIdx;
      this.draggingInfo = this.getDropPosition(componentType, targetRow, targetRowEl, event.pageX, event.pageY);
    },
    onDragLeave() {
      this.draggingInfo = null;
      this.draggingRowIndex = -1;
    },
    onDragEnd() {
      this.draggingComponent = null;
    },
    onDropBetween(event, rowIdx) {
      if (!this.draggingComponent) {
        return;
      }

      // Compute where to insert it
      const componentType = this.draggingComponent.type;
      const targetRow = this.layoutRows[rowIdx];
      const targetRowEl = event.currentTarget;
      const dropInfo = this.getDropPosition(componentType, targetRow, targetRowEl, event.pageX, event.pageY);
      this.draggingInfo = null;
      this.draggingRowIndex = -1;
      if (!dropInfo) {
        this.draggingComponent = null;
        return;
      }

      // Insert a clone
      const componentClone = structuredClone(this.draggingComponent);
      componentClone._id = this.generateRandomId('internal', 10);
      if (dropInfo.position === 'ABOVE') {
        console.info('INSERT AT TOP')
        this.insertNewRowAt(rowIdx, componentClone);
      } else if (dropInfo.position === 'BELOW') {
        console.info('INSERT AT BOTTOM')
        this.insertNewRowAt(rowIdx + 1, componentClone);
      } else if (dropInfo.position === 'IN') {
        const row = this.layoutRows[rowIdx];
        row.components.splice(dropInfo.columnIndex, 0, componentClone);
        console.info(`INSERTED IN ROW ${rowIdx} AT INDEX ${dropInfo.columnIndex}`);
      }

      // Select the component
      this.selectedComponentIds.clear();
      this.selectedComponentIds.add(componentClone._id);

      // Remove component from layout if its there
      this.layoutRows = this.layoutRows.map(row => {
        return {
          ...row,
          components: row.components.filter(comp => comp != this.draggingComponent)
        }
      }).filter(row => row.components.length > 0)

      this.draggingComponent = null;
    },
    onClickComponent(event, component) {
      if (event.shiftKey) {
        if (this.selectedComponentIds.has(component._id)) {
          this.selectedComponentIds.delete(component._id);
        } else {
          this.selectedComponentIds.add(component._id);
        }
      } else if (!this.selectedComponentIds.has(component._id)) {
        this.selectedComponentIds.clear();
        this.selectedComponentIds.add(component._id);
      }
    },
    unselectComponents() {
      this.selectedComponentIds.clear();
    },
    deleteComponents() {
      // Rip it out of the form if it exists
      this.layoutRows = this.layoutRows.map(row => {
        return {
          ...row,
          components: row.components.filter(comp => !this.selectedComponentIds.has(comp._id))
        }
      }).filter(row => row.components.length > 0)

      this.selectedComponentIds.clear();
    },
    getDropPosition(componentType, row, rowElement, pageX, pageY) {
      const VPAD = 18;
      const rect = rowElement.getBoundingClientRect();

      // Check if dropping above the row
      if (pageY < rect.top + VPAD) {
        return { position: 'ABOVE', columnIndex: null };
      }

      // Check if dropping below the row
      if (pageY > rect.bottom - VPAD) {
        return { position: 'BELOW', columnIndex: null };
      }

      // Block mixing single-childs with other components
      const singleChildTypes = ['HEADER', 'PARAGRAPH', 'IMAGE']
      const components = row.components;
      const hasBlocker = singleChildTypes.includes(componentType) || components.some(comp => singleChildTypes.includes(comp.type));
      if (hasBlocker) return null;

      //Dropping inside the row - determine column index
      const children = rowElement.querySelectorAll('.row-column');
      const rects = Array.from(children).map(el => el.getBoundingClientRect());
      let columnIndex = rects.length; // Default to end

      for (let i = 0; i < rects.length; i++) {
        const childRect = rects[i];
        if (pageX < childRect.left + childRect.width / 2) {
          columnIndex = i;
          break;
        }
      }

      return { position: 'IN', columnIndex };
    },
    onDragOverAfter() {
      this.draggingRowIndex = this.layoutRows.length - 1;
      this.draggingInfo = { position: 'BELOW' }
    },
    onDropAfter() {
      if (!this.draggingComponent) {
        return;
      }

      // Create new row
      const componentClone = structuredClone(this.draggingComponent);
      if (!componentClone._id) {
        componentClone._id = this.generateRandomId('internal', 10);
      }
      const newRow = {
        key: this.generateRandomId('row'),
        singleChild: false,
        components: [componentClone]
      }

      this.layoutRows.push(newRow);

      // Rip it out of the form if it exists
      this.layoutRows = this.layoutRows.map(row => {
        return {
          ...row,
          components: row.components.filter(comp => comp != this.draggingComponent)
        }
      }).filter(row => row.components.length > 0)

      this.draggingComponent = null;
      this.draggingInfo = null;
      this.draggingRowIndex = -1;

      this.selectedComponentIds.clear();
      this.selectedComponentIds.add(componentClone._id);
    },
    insertNewRowAt(idx, component) {
      // Create new row
      const newRow = {
        key: this.generateRandomId('row'),
        singleChild: false,
        components: [component]
      }

      // Insert at index
      this.layoutRows = [
        ...this.layoutRows.slice(0, idx),
        newRow,
        ...this.layoutRows.slice(idx)
      ]
    },
    generateRandomId(prefix, length = 10) {
      let id = prefix;
      for (let i = 0; i < length; i++) {
        id += Math.floor(Math.random() * 10);
      }
      return id;
    },
    ensureInternalIds() {
      this.layoutRows.forEach(row => {
        row.components.forEach(component => {
          if (!component._id) {
            component._id = this.generateRandomId('internal', 10);
          }
        });
      });
    },
    getMarkerForRow(rowIdx) {
      if (!this.draggingInfo || this.draggingRowIndex == -1) return;

      if (this.draggingInfo.position == 'ABOVE') {
        if (rowIdx == this.draggingRowIndex) return 'UP';
        if (rowIdx == this.draggingRowIndex - 1) return 'DOWN';
      }

      if (this.draggingInfo.position == 'BELOW') {
        if (rowIdx == this.draggingRowIndex) return 'DOWN';
        if (rowIdx == this.draggingRowIndex + 1) return 'UP';
      }
    },
    getMarkerForCol(rowIdx, colIdx) {
      if (!this.draggingInfo || this.draggingRowIndex == -1) return;
      if (rowIdx != this.draggingRowIndex || this.draggingInfo.position != 'IN') return;

      if (colIdx == this.draggingInfo.columnIndex) return 'LEFT';
      if (colIdx == this.draggingInfo.columnIndex - 1) return 'RIGHT';
    },
    isComponentSelected(component) {
      return this.selectedComponentIds.has(component._id);
    },
    getAllComponents() {
      const components = [];

      for (let row of this.layoutRows) {
        for (let component of row.components) {
          components.push(component);
        }
      }

      return components;
    },
    getSelectedComponents() {
      return this.getAllComponents().filter(comp => this.isComponentSelected(comp));
    },
    handleUpdateComponents(components) {
      // Create a map of component ID to updated component
      const componentMap = new Map();
      components.forEach(comp => {
        componentMap.set(comp._id, comp);
      });

      // Update the layout by replacing components with matching IDs
      this.layoutRows = this.layoutRows.map(row => {
        return {
          ...row,
          components: row.components.map(component => {
            if (componentMap.has(component._id)) {
              return componentMap.get(component._id);
            }
            return component;
          })
        };
      });
    }
  },
  components: { ComponentSidebarV2, PropertiesSidebarV2, HtmlComponent }
}
</script>

<style scoped>
.full {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow-y: hidden;
}

.form-pages {
  padding: 15px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.page {
  width: 100%;
  max-width: 1200px;
  flex-shrink: 0;
  background: #fff;
  box-shadow: 0px 0px 8px #00000022;
  padding: 0px;
  padding-top: 25px;
  min-height: 400px;
}

.page-row {
  padding: 18px 75px;
}

.row-columns {
  position: relative;
  display: flex;
  gap: 25px;
  align-items: flex-end;
}

.row-column {
  position: relative;
  flex: 1;
}

.drop-zone {
  width: 100%;
  height: 200px;
}

[data-selected="true"] {
  outline: 2px dashed green;
  box-shadow: inset 0px 0px 0px 250px #00FF0055;
}

[data-conditional="true"] {
  opacity: 0.5;
}

[data-marker="UP"]::after {
  content: "";
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  border-top: 2px dashed blue;
}

[data-marker="DOWN"]::after {
  content: "";
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: 100%;
  border-bottom: 2px dashed blue;
}

[data-marker="RIGHT"]::after {
  content: "";
  position: absolute;
  top: 0px;
  right: 0px;
  height: 100%;
  border-right: 2px dashed blue;
}

[data-marker="LEFT"]::after {
  content: "";
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  border-left: 2px dashed blue;
}

[data-dragging="true"] {
  opacity: 0.2;
}
</style>